
import { useState, useEffect } from 'react';
import { Star, Filter, ArrowUpDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';

interface Review {
  id: string;
  rating: number;
  review_text?: string;
  created_at: string;
  course_type?: string;
  reviewer: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

interface MenteeReviewsSectionProps {
  menteeId: string;
}

const REVIEWS_PER_PAGE = 3;

const MenteeReviewsSection = ({ menteeId }: MenteeReviewsSectionProps) => {
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([]);
  const [displayedReviews, setDisplayedReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [courseTypeFilter, setCourseTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    fetchReviews();
  }, [menteeId]);

  useEffect(() => {
    applyFiltersAndSorting();
  }, [allReviews, courseTypeFilter, sortBy]);

  useEffect(() => {
    const endIndex = currentPage * REVIEWS_PER_PAGE;
    setDisplayedReviews(filteredReviews.slice(0, endIndex));
  }, [filteredReviews, currentPage]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      console.log(`Fetching reviews for mentee: ${menteeId}`);
      
      // Fetch appointment reviews (voice/video call reviews)
      const { data: appointmentReviewsData, error: appointmentError } = await supabase
        .from('appointment_reviews')
        .select('id, rating, review_text, created_at, reviewer_id, booking_id')
        .eq('reviewee_id', menteeId)
        .order('created_at', { ascending: false });

      // Fetch subscription reviews
      const { data: subscriptionReviewsData, error: subscriptionError } = await supabase
        .from('subscription_reviews')
        .select('id, rating, review_text, created_at, reviewer_id, application_id')
        .eq('reviewee_id', menteeId)
        .eq('review_type', 'mentor_to_mentee')
        .order('created_at', { ascending: false });

      console.log(`Found ${appointmentReviewsData?.length || 0} appointment reviews and ${subscriptionReviewsData?.length || 0} subscription reviews for mentee`);

      if (appointmentError) {
        console.error('Error fetching appointment reviews:', appointmentError);
        return;
      }

      if (subscriptionError) {
        console.error('Error fetching subscription reviews:', subscriptionError);
        return;
      }

      const transformedReviews: Review[] = [];
      
      // Process appointment reviews
      if (appointmentReviewsData && appointmentReviewsData.length > 0) {
        for (const review of appointmentReviewsData) {
          // Fetch reviewer profile
          const { data: reviewer } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', review.reviewer_id)
            .maybeSingle();

          // For appointment reviews, get course info via session_bookings -> applications -> courses
          const { data: sessionBooking } = await supabase
            .from('session_bookings')
            .select('application_id')
            .eq('id', review.booking_id)
            .maybeSingle();

          let courseType = 'Voice/Video Call';
          if (sessionBooking?.application_id) {
            const { data: application } = await supabase
              .from('applications')
              .select('course_id')
              .eq('id', sessionBooking.application_id)
              .maybeSingle();

            if (application?.course_id) {
              const { data: course } = await supabase
                .from('courses')
                .select('course_type')
                .eq('id', application.course_id)
                .maybeSingle();

              courseType = course?.course_type || 'Voice/Video Call';
            }
          }

          transformedReviews.push({
            id: review.id,
            rating: review.rating,
            review_text: review.review_text,
            created_at: review.created_at,
            course_type: courseType,
            reviewer: {
              first_name: reviewer?.first_name || 'Anonymous',
              last_name: reviewer?.last_name || '',
              avatar_url: reviewer?.avatar_url || ''
            }
          });
        }
      }

      // Process subscription reviews
      if (subscriptionReviewsData && subscriptionReviewsData.length > 0) {
        for (const review of subscriptionReviewsData) {
          // Fetch reviewer profile
          const { data: reviewer } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', review.reviewer_id)
            .maybeSingle();

          // Fetch application to get course information
          const { data: application } = await supabase
            .from('applications')
            .select('course_id')
            .eq('application_id', review.application_id)
            .maybeSingle();

          let courseType = 'subscription';
          if (application?.course_id) {
            const { data: course } = await supabase
              .from('courses')
              .select('course_type')
              .eq('id', application.course_id)
              .maybeSingle();

            courseType = course?.course_type || 'subscription';
          }

          transformedReviews.push({
            id: review.id,
            rating: review.rating,
            review_text: review.review_text,
            created_at: review.created_at,
            course_type: courseType,
            reviewer: {
              first_name: reviewer?.first_name || 'Anonymous',
              last_name: reviewer?.last_name || '',
              avatar_url: reviewer?.avatar_url || ''
            }
          });
        }
      }

      // Sort combined reviews by creation date (most recent first)
      transformedReviews.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setAllReviews(transformedReviews);
      
      // Calculate average rating
      if (transformedReviews.length > 0) {
        const totalRating = transformedReviews.reduce((sum, review) => sum + review.rating, 0);
        setAverageRating(totalRating / transformedReviews.length);
      } else {
        setAverageRating(0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSorting = () => {
    let filtered = [...allReviews];

    // Apply course type filter
    if (courseTypeFilter !== 'all') {
      filtered = filtered.filter(review => {
        const reviewCourseType = review.course_type?.toLowerCase();
        
        if (courseTypeFilter === 'subscription') {
          return reviewCourseType === 'subscription';
        } else if (courseTypeFilter === 'call') {
          return reviewCourseType === 'call' || 
                 reviewCourseType === 'voice/video call' || 
                 reviewCourseType === 'voicecall' ||
                 reviewCourseType === 'voice call' ||
                 reviewCourseType === 'video call';
        }
        return true;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'rating-desc':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'rating-asc':
        filtered.sort((a, b) => a.rating - b.rating);
        break;
      default:
        break;
    }

    setFilteredReviews(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleShowMore = async () => {
    setLoadingMore(true);
    
    // Simulate a small delay for smooth transition
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setCurrentPage(prev => prev + 1);
    setLoadingMore(false);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating
            ? "text-yellow-400 fill-yellow-400"
            : "text-gray-300"
        }`}
      />
    ));
  };

  // Helper function to format course type for display
  const formatCourseType = (courseType: string) => {
    const lowerType = courseType.toLowerCase();
    if (lowerType === "subscription") {
      return "Subscription";
    } else if (lowerType === "voicecall" || lowerType === "voice call" || lowerType === "video call" || lowerType === "call" || lowerType === "voice/video call") {
      return "Voice/Video Call";
    }
    return courseType;
  };

  const hasMoreReviews = displayedReviews.length < filteredReviews.length;

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Reviews</h2>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Reviews</h2>
        
        {allReviews.length > 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center">
                {renderStars(Math.round(averageRating))}
              </div>
              <span className="text-lg font-semibold">
                {averageRating.toFixed(1)} / 5
              </span>
              <span className="text-gray-500">
                ({allReviews.length} review{allReviews.length !== 1 ? 's' : ''})
              </span>
            </div>
          </div>
        )}

        {/* Filter and Sort Controls */}
        {allReviews.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={courseTypeFilter} onValueChange={setCourseTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by course" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="call">Voice/Video Call</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="rating-desc">Highest Rating</SelectItem>
                  <SelectItem value="rating-asc">Lowest Rating</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {filteredReviews.length > 0 ? (
        <div className="space-y-4">
          <div className="space-y-4 transition-all duration-300 ease-in-out">
            {displayedReviews.map((review, index) => (
              <Card 
                key={review.id} 
                className="border border-gray-200 transition-all duration-300 ease-in-out"
                style={{
                  animation: `fade-in 0.3s ease-out ${index * 0.1}s both`
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage 
                        src={review.reviewer?.avatar_url} 
                        alt={`${review.reviewer?.first_name}'s avatar`}
                      />
                      <AvatarFallback>
                        {review.reviewer?.first_name?.[0] || "U"}
                        {review.reviewer?.last_name?.[0] || ""}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {review.reviewer?.first_name} {review.reviewer?.last_name}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            {renderStars(review.rating)}
                          </div>
                          {review.course_type && (
                            <div className="text-xs text-gray-500 mt-1">
                              {formatCourseType(review.course_type)}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                      
                      {review.review_text && (
                        <p className="text-gray-700 leading-relaxed">
                          {review.review_text}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMoreReviews && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={handleShowMore}
                disabled={loadingMore}
                className="px-6 py-2 transition-all duration-200 hover:bg-gray-50"
              >
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  `Show More Reviews (${filteredReviews.length - displayedReviews.length} remaining)`
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">
            <Star className="h-12 w-12 mx-auto text-gray-300" />
          </div>
          <p className="text-gray-500 italic">
            {courseTypeFilter !== "all" ? "No reviews found for the selected filter." : "No reviews yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default MenteeReviewsSection;
